import type { QueryClient } from '@tanstack/react-query';

import { clearOfflineData } from '~/offline/draftDb';
import { forgetDocumentVisits } from '~/session/documentVisits';
import { STORAGE_KEYS } from '~/storage-keys';
import { useAuthStore } from '~/stores/auth';
import { useCompanyStore } from '~/stores/company';

/**
 * Apaga tudo que identifica o usuário no aparelho: sessão, empresa selecionada, cache de
 * consultas persistido, dados offline (rascunhos e emissões pendentes) e a última visita ao feed
 * de documentos de cada empresa (`rx-documents-seen:<id>`). Usado no logout e
 * quando outro usuário entra no mesmo navegador, para que ninguém veja notas de quem usou
 * o app antes.
 *
 * O escritório do endereço (`rx-tenant`) fica: é da origem, não do usuário, e sem ele a tela de
 * login do próximo usuário esperaria o backend (ou não abriria offline).
 *
 * A sessão é limpa antes das chaves do storage: o middleware `persist` regrava
 * `rx-auth` a cada mudança de estado, e a ordem inversa deixaria os tokens antigos
 * de volta no disco.
 */
export async function clearLocalData(queryClient: QueryClient): Promise<void> {
  useAuthStore.getState().clearSession();
  useCompanyStore.getState().clear();
  queryClient.clear();
  await clearOfflineData();
  try {
    localStorage.removeItem(STORAGE_KEYS.queryCache);
    localStorage.removeItem(STORAGE_KEYS.auth);
    localStorage.removeItem(STORAGE_KEYS.selectedCompany);
  } catch {
    /* storage indisponível (modo privado restrito): nada gravado para apagar */
  }
  forgetDocumentVisits();
}
